import { Button } from "@/components/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/tabs";
import { cn } from "@/lib/utils";
import {
  type CellField,
  type ExportExcelConfig,
  type Model,
  getChildFields,
  getListModelPaths,
} from "@repo/shared/workflow";
import {
  type CellObject,
  type WorkBook,
  type WorkSheet,
  read,
  utils,
} from "@sheet/coredemo";
import { ArrowDown, ArrowRight, Plus, Rows4, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/select";
import { produce } from "immer";

interface ExcelViewerProps {
  rawBytes: ArrayBuffer;
  config: ExportExcelConfig;
  onConfigChange?: (config: ExportExcelConfig) => void;
  model: Model;
}

interface CellStyle extends React.CSSProperties {
  minWidth?: string;
  height?: string;
}

// Helper Functions
const getCellDimensions = (
  ws: WorkSheet,
  row: number,
  col: number,
): { width: string; height: string } => {
  const colObj = ws["!cols"]?.[col];
  const rowObj = ws["!rows"]?.[row];

  const width = colObj?.wpx
    ? `${colObj.wpx}px`
    : colObj?.wch
      ? `${colObj.wch * 7}px`
      : "64px";

  const height = rowObj?.hpx
    ? `${rowObj.hpx}px`
    : rowObj?.hpt
      ? `${(rowObj.hpt * 4) / 3}px`
      : "22px";

  return { width, height };
};

const getCellConfig = (
  row: number,
  col: number,
  selectedSheet: string,
  config: ExportExcelConfig,
): CellField | null => {
  const sheetData = config.editTemplate.sheets.find(
    (sheet) => sheet.name === selectedSheet,
  )?.sheet;

  if (!sheetData?.lists || !selectedSheet) return null;

  const listField =
    sheetData.lists
      .flatMap((list) =>
        list.fields.map((field) => ({
          ...field,
          list_name: list.modelName,
        })),
      )
      .find(
        (field) => field.cell.row === row + 1 && field.cell.column === col + 1,
      ) || null;
  if (listField) return listField;

  const cellField = sheetData.cells.find(
    (cell) => cell.cell.row === row + 1 && cell.cell.column === col + 1,
  );
  if (cellField) return cellField;
  return null;
};

type CellDisplayState = "can-be-added" | "in-list" | "in-cell";
type ListContinuationState = "row" | "column";

const ExcelCell: React.FC<{
  cell: CellObject;
  style: CellStyle;
  config: CellField | null;
  listChildVariables: string[];
  onClick?: () => void;
  onAddToListClick?: () => void;
  onSelectModelPath?: (variable: string) => void;
  selectable?: boolean;
  displayState?: CellDisplayState;
  listContinuationState?: ListContinuationState;
  onRemoveFromListClick?: () => void;
  isHovered?: boolean;
}> = ({
  cell,
  style,
  config,
  listChildVariables,
  onClick,
  onAddToListClick,
  onSelectModelPath,
  selectable = false,
  displayState,
  listContinuationState,
  onRemoveFromListClick,
  isHovered,
}) => {
  const cellText = cell?.v !== undefined ? String(cell.v) : "";

  return (
    <td
      style={{ ...style, position: "relative", height: 28 }}
      onClick={onClick}
      className={cn("group", {
        "bg-blue-100": displayState === "in-list",
        "cursor-pointer z-10 hover:bg-blue-200": selectable,
        "bg-blue-50": listContinuationState,
        "bg-green-100": isHovered,
      })}
    >
      {cellText}
      {listContinuationState === "column" && (
        <div>
          <ArrowRight className="w-4 h-4 text-blue-700" />
        </div>
      )}
      {listContinuationState === "row" && (
        <div>
          <ArrowDown className="w-4 h-4 text-blue-700" />
        </div>
      )}
      {displayState === "can-be-added" && (
        <div className="absolute inset-0 flex items-center justify-end px-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="outline"
            size="flush"
            className="px-1.5 py-0.5"
            onClick={(e) => {
              e.stopPropagation();
              onAddToListClick?.();
            }}
          >
            Add
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}
      {displayState === "in-list" && (
        <>
          <Select
            value={config?.modelPath}
            onValueChange={(value) => {
              onSelectModelPath?.(value);
            }}
          >
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select a variable" />
            </SelectTrigger>
            <SelectContent className="max-h-[480px] overflow-auto">
              {listChildVariables
                .filter((v) => !!v)
                .map((variable) => (
                  <SelectItem
                    key={variable}
                    value={variable}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {variable}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {/* Remove from list button */}
          <div className="absolute top-0 -right-9 bottom-0 z-10 flex items-center justify-end px-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="outline"
              size="flush"
              className="p-2 text-red-500 hover:text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFromListClick?.();
              }}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </>
      )}
    </td>
  );
};

type EditState =
  | "idle"
  | "selecting-list-add"
  | "selecting-cell-add"
  | "editing-list"
  | "editing-cell";

type Cell = {
  row: number;
  col: number;
};

export const ExcelTemplateEditor: React.FC<ExcelViewerProps> = ({
  rawBytes,
  config,
  onConfigChange,
  model,
}) => {
  // General state
  const [workbook, setWorkbook] = useState<WorkBook | null>(null);
  const [selectedSheetName, setSelectedSheetName] = useState<string | null>(
    null,
  );
  const selectedSheet = useMemo(() => {
    return config.editTemplate.sheets.find(
      (sheet) => sheet.name === selectedSheetName,
    );
  }, [config, selectedSheetName]);

  const [selectedCell, setSelectedCell] = useState<Cell | null>(null);
  const [editState, setEditState] = useState<EditState>("idle");
  const [selectedCellFieldIdx, setSelectedCellFieldIdx] = useState<
    number | null
  >(null);
  const [selectedListIdx, setSelectedListIdx] = useState<number | null>(null);
  const [hoveredListIdx, setHoveredListIdx] = useState<number | null>(null);

  const selectedList = useMemo(() => {
    if (selectedListIdx == null) return null;
    return selectedSheet?.sheet.lists[selectedListIdx];
  }, [selectedListIdx, selectedSheet]);

  // TODO: Add cell field selection
  // const selectedCellField = useMemo(() => {
  //   if (selectedCellFieldIdx == null) return null;
  //   return selectedList?.fields[selectedCellFieldIdx];
  // }, [selectedCellFieldIdx, selectedList]);

  const handleClearSelection = useCallback(() => {
    setEditState("idle");
    setSelectedListIdx(null);
    setSelectedCellFieldIdx(null);
    setHoveredListIdx(null);
  }, []);

  useEffect(() => {
    const wb = read(rawBytes, { cellStyles: true });
    setWorkbook(wb);
  }, [rawBytes]);

  useEffect(() => {
    const enabledSheets = config.editTemplate.sheets.filter(
      (sheet) => sheet.enabled,
    );
    const firstEnabledSheet = enabledSheets[0]?.name;
    const firstSheet = config.editTemplate.sheets[0]?.name;

    const sheetNameMissingInConfig = !config.editTemplate.sheets.find(
      (sheet) => sheet.name === selectedSheetName,
    );

    if (!selectedSheetName || sheetNameMissingInConfig) {
      setSelectedSheetName(firstEnabledSheet ?? firstSheet ?? null);
    }
  }, [config, selectedSheetName]);

  const renderSheet = (ws: WorkSheet) => {
    if (!ws || !ws["!ref"]) {
      return <div>No data or invalid sheet ref</div>;
    }

    const range = utils.decode_range(ws["!ref"]);
    const rows: JSX.Element[] = [];

    for (let row = range.s.r; row <= range.e.r; row++) {
      const rowCells: JSX.Element[] = [];

      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = utils.encode_cell({ r: row, c: col });
        const cell = ws[cellAddress];
        const { width, height } = getCellDimensions(ws, row, col);

        const style: CellStyle = {
          border: "1px solid #ccc",
          padding: "4px",
          minWidth: width,
          height: height,
        };

        const cellConfig = selectedSheetName
          ? getCellConfig(row - 1, col - 1, selectedSheetName, config)
          : null;

        const selectingAny = editState.includes("selecting");
        const parentList = selectedSheet?.sheet.lists.find((list) =>
          list.fields.some(
            (field) => field.cell.row === row && field.cell.column === col,
          ),
        );
        const inAnyList = parentList != null;
        const inCurrentList = selectedList?.fields.some(
          (field) => field.cell.row === row && field.cell.column === col,
        );

        const considerLists =
          selectedList != null ? [selectedList] : selectedSheet?.sheet.lists;
        const nearbyList = considerLists?.find((list) => {
          const delta =
            list.direction === "column" ? { x: -1, y: 0 } : { x: 0, y: -1 };
          const checkCell = {
            row: row + delta.y,
            col: col + delta.x,
          };

          const nearbyCellInList = list.fields.some(
            (field) =>
              field.cell.row === checkCell.row &&
              field.cell.column === checkCell.col,
          );
          return nearbyCellInList ? list : undefined;
        });

        const selectedListVariables = (() => {
          if (parentList == null) return [];
          if (model.type !== "object") return [];
          const listModelName = parentList.modelName;
          let listModel = model.fields.find(
            (field) => field.name === listModelName && field.type === "array",
          );

          if (!listModel && parentList.modelName.split(".").length === 2) {
            const [first, second] = parentList.modelName.split(".");
            listModel = model.fields.find(
              (field) => field.name === first && field.type === "object",
            );

            if (listModel && listModel.type === "object") {
              listModel = listModel.fields.find(
                (field) => field.name === second && field.type === "array",
              );
            }
          }

          if (!listModel || listModel.type !== "array") return [];

          const subVariables = getChildFields(listModel.model);

          return subVariables;
        })();

        let displayState: CellDisplayState | undefined;
        let listContinuationState: ListContinuationState | undefined;
        // If a list is selected, show if a cell is in the list or can be added to the list
        if (selectedList != null) {
          if (inCurrentList) {
            displayState = "in-list";
          } else if (!inAnyList) {
            displayState = "can-be-added";
            listContinuationState = nearbyList?.direction;
          }
        } else if (inAnyList) {
          // if a list is not selected, but a cell is in a list, show that
          displayState = "in-list";
        } else {
          // if this cell is not in a list, but will be filled by the list process
          // show that
          listContinuationState = nearbyList?.direction;
        }

        // Add check for if cell is in hovered list
        const inHoveredList =
          hoveredListIdx !== null &&
          selectedSheet?.sheet.lists[hoveredListIdx]?.fields.some(
            (field) => field.cell.row === row && field.cell.column === col,
          );

        rowCells.push(
          <ExcelCell
            key={col}
            cell={cell}
            style={style}
            config={cellConfig}
            listChildVariables={selectedListVariables}
            onClick={() => handleCellClick(row, col)}
            onAddToListClick={() => handleAddCellToList(row, col)}
            onRemoveFromListClick={() => handleRemoveCellFromList(row, col)}
            onSelectModelPath={(variable) =>
              handleSelectModelPath(row, col, variable)
            }
            selectable={selectingAny}
            displayState={displayState}
            listContinuationState={listContinuationState}
            isHovered={inHoveredList}
          />,
        );
      }

      rows.push(<tr key={row}>{rowCells}</tr>);
    }

    return (
      <table style={{ borderCollapse: "collapse" }}>
        <tbody>{rows}</tbody>
      </table>
    );
  };

  const handleCellClick = useCallback(
    (row: number, column: number) => {
      if (!selectedSheet || !selectedSheetName) return;

      if (editState === "selecting-cell-add") {
        // We're adding a cell
        onConfigChange?.(
          produce(config, (draft) => {
            const sheet = draft.editTemplate.sheets.find(
              (s) => s.name === selectedSheet.name,
            );
            if (sheet) {
              sheet.sheet.cells.push({
                cell: { row, column },
                modelPath: "",
              });
            }
          }),
        );

        setSelectedCellFieldIdx(selectedSheet.sheet.cells.length);
      } else if (editState === "selecting-list-add") {
        // We're adding a list
        onConfigChange?.(
          produce(config, (draft) => {
            const sheet = draft.editTemplate.sheets.find(
              (s) => s.name === selectedSheet.name,
            );
            if (sheet) {
              const firstVariable = listVariables[0];
              sheet.sheet.lists.push({
                direction: "column",
                modelName: firstVariable.join("."),
                fields: [{ cell: { row, column }, modelPath: "" }],
              });
            }
          }),
        );

        setSelectedListIdx(selectedSheet.sheet.lists.length);
        setEditState("editing-list");
      }
    },
    [editState, selectedSheet, config, onConfigChange, selectedSheetName],
  );

  const handleAddCellToList = useCallback(
    (row: number, col: number) => {
      if (!selectedList || !selectedSheetName || selectedListIdx === null)
        return;

      onConfigChange?.(
        produce(config, (draft) => {
          const sheet = draft.editTemplate.sheets.find(
            (s) => s.name === selectedSheetName,
          );
          if (!sheet) return;

          const list = sheet.sheet.lists[selectedListIdx];
          if (!list) return;

          list.fields.push({
            cell: { row, column: col },
            modelPath: "",
          });
        }),
      );
    },
    [config, onConfigChange, selectedList, selectedListIdx, selectedSheetName],
  );

  const handleRemoveCellFromList = useCallback(
    (row: number, col: number) => {
      if (!selectedSheetName) return;

      onConfigChange?.(
        produce(config, (draft) => {
          const sheet = draft.editTemplate.sheets.find(
            (s) => s.name === selectedSheetName,
          );
          if (!sheet) return;

          for (const list of sheet.sheet.lists) {
            const fieldIndex = list.fields.findIndex(
              (field) => field.cell.row === row && field.cell.column === col,
            );
            if (fieldIndex !== -1) {
              list.fields.splice(fieldIndex, 1);
              break;
            }
          }
        }),
      );
    },
    [config, onConfigChange, selectedSheetName],
  );

  const handleSelectModelPath = useCallback(
    (row: number, col: number, variable: string) => {
      if (!selectedSheetName) return;

      onConfigChange?.(
        produce(config, (draft) => {
          const sheet = draft.editTemplate.sheets.find(
            (s) => s.name === selectedSheetName,
          );
          if (!sheet) return;

          // Find the list and field that contains this cell
          for (const list of sheet.sheet.lists) {
            const fieldIndex = list.fields.findIndex(
              (field) => field.cell.row === row && field.cell.column === col,
            );
            if (fieldIndex !== -1) {
              // Update the model path for this field
              list.fields[fieldIndex].modelPath = variable;
              break;
            }
          }
        }),
      );
    },
    [config, onConfigChange, selectedSheetName],
  );

  const listVariables = useMemo(() => {
    return getListModelPaths(model);
  }, [model]);

  if (!workbook) return null;

  return (
    <div className="grid grid-cols-[1fr,3fr] border border-gray-200 rounded-lg py-3 gap-x-2 max-w-full">
      {/* Sidebar */}
      <div className="min-w-0 max-w-[320px] p-4 rounded-sm">
        {/* {!selectedCell && (
          <div className="p-4 text-sm text-gray-500 text-center bg-gray-50 m-4 border rounded">
            Select a cell to continue
          </div>
        )}
        {selectedCell && (
          <div className="p-4 text-sm text-gray-500 text-center bg-gray-50 m-4 border rounded">
            {selectedCell.row}, {selectedCell.col}
          </div>
        )} */}
        {/* <div>{editState}</div> */}
        {editState === "idle" && (
          <div className="flex flex-col gap-2">
            {/* <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleAddCell}
            >
              <RectangleHorizontal className="w-4 h-4 mr-2" />
              Add Variable
            </Button> */}
            <div>
              <div className="mt-4">
                <p className="font-medium mb-2">Lists</p>
                <div className="flex flex-col gap-1">
                  {selectedSheet?.sheet.lists.map((list, idx) => (
                    <Button
                      key={`${list.modelName}-${idx}`}
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => {
                        setEditState("editing-list");
                        setSelectedListIdx(idx);
                      }}
                      onMouseEnter={() => setHoveredListIdx(idx)}
                      onMouseLeave={() => setHoveredListIdx(null)}
                    >
                      <Rows4 className="w-4 h-4 mr-2" />
                      {list.modelName || "Unnamed List"}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <Button
              variant="link"
              size="sm"
              className="w-fit"
              onClick={() => {
                setEditState("selecting-list-add");
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add List
            </Button>
          </div>
        )}
        {editState.includes("selecting") && (
          <div className="text-sm text-gray-500 text-center bg-gray-50 border rounded py-8">
            Select a cell to start a list
          </div>
        )}
        {editState === "editing-list" && (
          <div className="">
            <div className="font-medium mb-2 flex items-center justify-between">
              <p>Editing List</p>
              <Button
                variant="ghost"
                size="flush"
                className="w-fit p-2"
                onClick={handleClearSelection}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <label htmlFor="list-direction" className="block mb-2 font-medium">
              Direction
            </label>
            <Tabs
              id="list-direction"
              value={selectedList?.direction}
              onValueChange={(value) => {
                onConfigChange?.(
                  produce(config, (draft) => {
                    const sheet = draft.editTemplate.sheets.find(
                      (s) => s.name === selectedSheet?.name,
                    );
                    if (!sheet) return;

                    const list = sheet.sheet.lists[selectedListIdx ?? 0];
                    if (!list) return;

                    list.direction = value as "row" | "column";
                  }),
                );
              }}
            >
              <TabsList>
                <TabsTrigger value="row">Row</TabsTrigger>
                <TabsTrigger value="column">Column</TabsTrigger>
              </TabsList>
            </Tabs>
            <label
              htmlFor="list-direction"
              className="block mt-4 mb-2 font-medium"
            >
              Data
            </label>
            <Select
              value={selectedList?.modelName}
              onValueChange={(value) => {
                onConfigChange?.(
                  produce(config, (draft) => {
                    const sheet = draft.editTemplate.sheets.find(
                      (s) => s.name === selectedSheet?.name,
                    );
                    if (!sheet) return;

                    const list = sheet.sheet.lists[selectedListIdx ?? 0];
                    if (!list) return;

                    list.modelName = value;
                  }),
                );
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a data source" />
              </SelectTrigger>
              <SelectContent>
                {listVariables.map((variable) => (
                  <SelectItem
                    key={variable.join(".")}
                    value={variable.join(".")}
                  >
                    {variable.join(".")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="link"
              size="sm"
              className="w-fit mt-8"
              onClick={() => {
                onConfigChange?.(
                  produce(config, (draft) => {
                    const sheet = draft.editTemplate.sheets.find(
                      (s) => s.name === selectedSheet?.name,
                    );
                    if (!sheet) return;

                    const list = sheet.sheet.lists[selectedListIdx ?? 0];
                    if (!list) return;

                    sheet.sheet.lists.splice(selectedListIdx ?? 0, 1);
                  }),
                );

                handleClearSelection();
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete List
            </Button>
          </div>
        )}
      </div>
      {/* Sheet View */}
      <div className="min-w-0">
        <Tabs
          value={selectedSheetName || ""}
          onValueChange={setSelectedSheetName}
        >
          <TabsList>
            {workbook.SheetNames.map((name) => (
              <TabsTrigger value={name} key={name}>
                {name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {selectedSheetName && (
          <div className="mt-2 border max-h-[720px] overflow-auto bg-white">
            {renderSheet(workbook.Sheets[selectedSheetName])}
          </div>
        )}
      </div>
    </div>
  );
};
